import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Heart,
  Lock,
  ScanFace,
  Sparkles as SparklesIcon,
  Mic,
  Eye,
  ShieldCheck,
  Quote,
  Check,
  Calendar,
  Mail,
  BookOpen,
  Wand2,
  Camera,
  Bookmark,
  ShoppingBag,
  ChevronRight,
  Menu,
  X,
  RotateCcw,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PrivacyIntro from "../components/PrivacyIntro";
import ConnectForm from "../components/ConnectForm";
import DiscoverShowcase from "../components/DiscoverShowcase";
import WaitlistBanner from "../components/WaitlistBanner";
import HeroTrouveChat from "../components/HeroTrouveChat";
import FirstRoutineNudge from "../components/FirstRoutineNudge";
import MeetTrouve from "../components/MeetTrouve";
// Feb 27 2026: RetargetingOverlay removed entirely — the "Stay in
// touch?" exit-intent modal + the bottom retarget banner interrupted
// the landing flow and were leaking broken i18n keys (retarget.banner.*).
// Wave 1 tightened the triggers; this pass deletes the surface.
// TrouveHeroVideo import removed Feb 8 2026 — the Sora 2 hero
// section was pulled per founder direction. The component file is
// retained on disk for possible future reuse but no longer mounted.
import { trackFP } from "../lib/firstPartyAnalytics";
// HomeInspirationDemo removed Feb 8 2026 — the recreate-a-look flow is
// now inline inside HomeConsultation. The file is retained on disk for
// possible standalone use (e.g. /recreate route) but no longer mounted
// on the homepage so the consultation is the single product entry.
import { useAuth } from "../context/AuthContext";
import { useReaderAppMode } from "../lib/readerAppMode";
import { useTranslation, Trans } from "react-i18next";

// Editorial press portrait of Iyal Basen — served from /public/img.
// `object-top` is applied in every place this renders so the head
// isn't cropped on taller containers.
const HERO_IMAGE = "/img/iyal-founder.jpg";

// `features` and `steps` arrays were previously hardcoded English at
// module scope. Moved into the component below (see useMemo) so each
// string flows through i18n's `t()` and the FR / ES SSR snapshots
// pick up translated titles + bodies. See `welcome.feature*` and
// `welcome.step*` keys in locales/{en,fr,es}.json.

// Static contact details for the founder. Display copy (name, role,
// bio paragraphs, pull quote, credential chips) lives in the
// founder.* i18n namespace so FR / ES SSR snapshots pick up the
// translated story.
const FOUNDER_CONTACT = {
  name: "Iyal Basen",
  contactEmail: "iyal@trubeaute.com",
  calendly: "https://calendly.com/iyalbasen/30min",
};

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
  viewport: { once: true, margin: "-50px" },
};

const SectionHeading = ({ eyebrow, children }) => (
  <div className="mb-12">
    {eyebrow ? (
      <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#9B6A6C] mb-3">
        {eyebrow}
      </span>
    ) : null}
    <h2 className="font-serif italic text-4xl sm:text-5xl leading-tight text-[#2C2123] max-w-3xl">
      {children}
    </h2>
  </div>
);

const Welcome = () => {
  const { t } = useTranslation();
  const [showIntro, setShowIntro] = useState(null);
  const readerApp = useReaderAppMode();
  const { user } = useAuth();

  // Public Demo Mode override: treat the auto-minted demo user as a
  // brand-new guest for the purpose of homepage layout + primary CTA
  // routing. Without this, the SignedInLaunchpad (whose primary card
  // routes to /capture) shows up as soon as the demo bootstrap mints
  // a demo user, sending brand/investor demos straight to the camera
  // step and skipping the trust-building quiz. The demo user IS
  // authenticated on the backend, so we can't just set user=null; we
  // instead compute a `showAsGuest` flag consumed by the layout branch
  // + the primary CTA. All returning REAL users continue to see the
  // Launchpad as before.
  //
  // Feb 27 2026 — Stealth/Guest fix: returning guest users (auth_provider
  // === "guest") were being shunted into the SignedInLaunchpad → /capture
  // path on revisit, skipping the trust-building quiz entirely. The
  // founder mandate is "the quiz must show up everywhere before the
  // camera" — so guest users are now folded into `showAsGuest` alongside
  // demo users. They see the GuestCaptureFirstLanding on every homepage
  // visit and the primary CTA routes them to /onboarding (where the
  // auto-skip logic still handles the "has saved prefs → bounce to
  // /capture" shortcut for guests who already finished the quiz).
  const isDemoMode =
    typeof window !== "undefined" && !!window.__trubeaute_demo_mode;
  // Guest detection: the User model does not currently expose
  // `auth_provider` on the /api/auth/me response, so we identify
  // guest sessions by the synthetic email suffix minted by the
  // backend (`guest_xxx@guest.trubeaute.local`). We keep the
  // provider-string check as a defensive belt-and-suspenders in
  // case the backend schema is extended later. This mirrors the
  // pattern already used by App.js (HausWelcomeMount) + backend
  // server.py `is_guest = user.email.endsWith("@guest.trubeaute.local")`.
  const _guestEmail =
    typeof user?.email === "string" &&
    user.email.toLowerCase().endsWith("@guest.trubeaute.local");
  const isGuestUser = _guestEmail || user?.auth_provider === "guest";
  const showAsGuest = !user || isDemoMode || isGuestUser;

  // Fire the canonical `homepage_viewed` event once per session
  // (the first step of the Feb 2026 conversion funnel). Auth state
  // is included as a property so the dashboard can split guest vs.
  // signed-in views without polluting the event name. Uses trackFP
  // so Stealth users still appear in the funnel.
  useEffect(() => {
    trackFP("homepage_viewed", { auth: user ? "signed_in" : "guest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // i18n-aware features + steps. The icon references stay co-located
  // with the strings so future content edits don't desync the icon.
  // Each row of these arrays is consumed by the corresponding section
  // below; ordering matters and matches the locales JSON sequencing.
  const features = React.useMemo(
    () => [
      {
        icon: ScanFace,
        title: t("welcome.feature1Title"),
        body: t("welcome.feature1Body"),
      },
      {
        icon: SparklesIcon,
        title: t("welcome.feature2Title"),
        body: t("welcome.feature2Body"),
      },
      {
        icon: ShoppingBag,
        title: t("welcome.feature3Title"),
        body: t("welcome.feature3Body"),
      },
      {
        icon: Lock,
        title: t("welcome.feature4Title"),
        body: t("welcome.feature4Body"),
      },
    ],
    [t],
  );

  const steps = React.useMemo(
    () => [
      { n: "1", title: t("welcome.step1Title"), body: t("welcome.step1Body") },
      { n: "2", title: t("welcome.step2Title"), body: t("welcome.step2Body") },
      { n: "3", title: t("welcome.step3Title"), body: t("welcome.step3Body") },
      { n: "4", title: t("welcome.step4Title"), body: t("welcome.step4Body") },
      { n: "5", title: t("welcome.step5Title"), body: t("welcome.step5Body") },
    ],
    [t],
  );

  // PLANS lives inside the component (not module-scope) so each
  // string flows through `t()` and the FR / ES SSR snapshots pick
  // up translated plan names, taglines, features, and CTAs.
  const PLANS = React.useMemo(
    () => [
      {
        name: t("welcome.planFreeName"),
        price: t("welcome.planFreePrice"),
        cadence: t("welcome.planFreeCadence"),
        tagline: t("welcome.pricingFreeTagline"),
        features: [
          t("welcome.planFreeFeature1"),
          t("welcome.planFreeFeature2"),
          t("welcome.planFreeFeature3"),
          t("welcome.planFreeFeature4"),
        ],
        cta: { to: "/onboarding", label: t("welcome.planFreeCta") },
        testid: "landing-plan-free",
        highlight: false,
      },
      {
        name: t("welcome.planProName"),
        price: t("welcome.planProPrice"),
        cadence: t("welcome.planProCadence"),
        tagline: t("welcome.pricingProTagline"),
        features: [
          t("welcome.planProFeature1"),
          t("welcome.planProFeature2"),
          t("welcome.planProFeature3"),
          t("welcome.planProFeature4"),
        ],
        cta: { to: "/pricing", label: t("welcome.planProCta") },
        testid: "landing-plan-pro",
        highlight: true,
      },
    ],
    [t],
  );

  // Primary CTA target depends on auth state.
  //   • Signed-in users go to /home (the launchpad).
  //   • Signed-out users create an account before entering onboarding.
  // This is the single primary path. The privacy intro modal is no
  // longer a gate on the primary CTA — it lives as an opt-in link
  // under the helper text per the Feb 2026 conversion sprint spec.
  const primaryCtaTo = showAsGuest ? "/login?signup=1&next=/onboarding" : "/home";
  const primaryCtaLabel = showAsGuest
    ? t("welcome.startPersonalizedRoutine")
    : t("welcome.ctaPrimaryAuthed");

  const navigate = useNavigate();

  // Signed-in users: navigate directly to /home (Link default).
  // Signed-out users: the Link routes them to account creation and then
  // returns them to onboarding via the sanitized `next` parameter.
  //
  // Demo Mode users: already authenticated (demo bootstrap minted
  // them). Skip the guest-mint and route directly to /onboarding so
  // brand/investor demos always start at the quiz, never the camera.
  //
  // Returning Guest users (Feb 27 2026 fix): they already hold a guest
  // session cookie from a previous visit. Do NOT mint a new one — just
  // navigate them to /onboarding so the quiz shows up before the
  // camera. This matches the founder mandate that stealth/guest users
  // must always see the quiz first, regardless of how many times they
  // return to the homepage.
  //
  // The `e` parameter is optional because this function is also passed
  // to <HeroTrouveChat onSkip={...}> (iter 79) which calls onSkip() with
  // no arguments — without the `?.` guard we'd throw inside an error
  // boundary when the skip link is tapped.
  const handlePrimaryCta = (e) => {
    if (user && !isDemoMode && !isGuestUser) {
      trackFP("homepage_primary_cta_clicked", { auth: "signed_in" });
      return; // Let the Link's default navigation to /home run
    }
    if (isDemoMode) {
      e?.preventDefault?.();
      trackFP("homepage_primary_cta_clicked", { auth: "demo" });
      navigate("/onboarding");
      return;
    }
    if (isGuestUser) {
      e?.preventDefault?.();
      trackFP("homepage_primary_cta_clicked", { auth: "guest_returning" });
      navigate("/onboarding");
      return;
    }
    // The hero's skip-chat control is a button, so it has no Link default
    // navigation to fall back on. Navigate explicitly for signed-out users;
    // preventDefault keeps the regular Link CTA from navigating twice.
    e?.preventDefault?.();
    trackFP("homepage_primary_cta_clicked", { auth: "signup_required" });
    navigate(primaryCtaTo);
  };
  const handlePrivacyIntroClose = () => {
    setShowIntro(null);
    if (!user) navigate(primaryCtaTo);
  };

  // ───────────── GUEST CAPTURE-FIRST LANDING ─────────────
  // For unauthenticated visitors we render a purpose-built landing
  // page (Feb 2026 capture-first redesign) that:
  //   • leads with a single "Start my free routine" CTA
  //   • communicates that the user can begin BEFORE signing up
  //   • uses the AR-mirror hero illustration as product proof
  //   • frames signup around save / personalize / return value
  //   • moves business/press/contact paths behind a hamburger
  // The signed-in path below this branch is intentionally untouched
  // — returning users keep their existing launchpad experience.
  //
  // Feb 27 2026 — Stealth/Guest fix: `showAsGuest` (which includes
  // demo-mode + guest-auth users) also gates this branch so returning
  // guest sessions always see the trust-building capture-first design
  // + the quiz-first CTA path, never the SignedInLaunchpad that would
  // route them straight to /capture.
  if (showAsGuest) {
    return (
      <>
        <GuestCaptureFirstLanding
          onPrimaryCta={handlePrimaryCta}
          primaryCtaTo={primaryCtaTo}
          onOpenPrivacyIntro={() => setShowIntro(true)}
          showIntro={showIntro}
          onCloseIntro={handlePrivacyIntroClose}
          readerApp={readerApp}
        />
      </>
    );
  }

  return (
    <div className="relative min-h-screen overflow-x-hidden trubeaute-gradient">
      <WaitlistBanner />
      <Header />

      {/* First-routine activation nudge — surfaces only for authed
          free users ≥24h post-signup with zero routines. We render
          it here (above the hero) so the very first thing they see
          on landing is the "your first routine is one selfie away"
          card, not the marketing pitch they've already absorbed. */}
      {user && !isDemoMode && (
        <div className="max-w-3xl mx-auto px-6 pt-28">
          <FirstRoutineNudge variant="welcome" />
        </div>
      )}

      {/* ───────────── SIGNED-IN DASHBOARD ─────────────
          For authenticated users only — replaces the pure marketing
          hero with a 4-card action board surfacing the highest-intent
          flows in the app:
            1. Take a selfie    → full personalized routine via GPT-4o
            2. Recreate a look  → ingest YouTube/TikTok/text tutorial
            3. Techniques       → 12 face-scan-free Pro tutorials
            4. My routines      → saved library

          Why above the marketing hero: a signed-in user has already
          bought the pitch — they don't need to be sold again. They
          need a launchpad to the thing they came here to do. The
          marketing hero stays available below for friend-shares /
          re-investment moments, but the actions are the headline.
      */}
      {user && !isDemoMode && (
        <SignedInLaunchpad userName={user.name} />
      )}

      {/* ───────────── HOME CONSULTATION (DEPRECATED INLINE SURFACE) ─────
          As of the Feb 2026 conversion sprint the homepage above-the-fold
          surfaces ONLY one primary CTA + one secondary CTA. The inline
          HomeConsultation has been folded BEHIND the primary CTA — clicking
          "Start your personalized routine" mints a guest session and
          routes the visitor into the streamlined 2Q + selfie flow on
          /onboarding. The hero is now marketing copy, not chat. */}

      {/* ───────────── HERO ─────────────
          Top-padding is dynamic:
            • Signed-in users (Launchpad above) → pt-8.
            • Guests (no Launchpad above) → pt-12 — give the hero room.
        */}
      <section
        className={`relative ${user ? "pt-8" : "pt-12"} pb-24 px-6 sm:px-10`}
        data-testid="welcome-hero"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <h1 className="text-6xl sm:text-7xl lg:text-[4.75rem] xl:text-[5rem] font-light text-[#2C2123] leading-[1.0] tracking-tight mb-8">
              {t("welcome.h1Prefix")}
              <span className="font-serif italic text-[#5D4050] whitespace-nowrap">
                {t("welcome.h1ItalicBefore")}
                <span className="not-italic font-medium tracking-tight">
                  {t("welcome.h1ItalicAfter")}
                </span>
              </span>
              .
            </h1>
            <p
              className="text-xl sm:text-2xl text-[#5C4B51] max-w-xl mb-10 leading-relaxed font-light"
              data-testid="welcome-subheadline"
            >
              {user ? (
                <Trans
                  i18nKey="welcome.subheadline"
                  components={{ em: <em className="font-serif" /> }}
                />
              ) : (
                t("welcome.subheadlineFast")
              )}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to={primaryCtaTo}
                onClick={handlePrimaryCta}
                className="group inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-8 py-4 text-base font-medium transition-all duration-300 shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5 disabled:opacity-60"
                data-testid="welcome-primary-cta"
              >
                {primaryCtaLabel}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/sample-routine"
                className="group inline-flex items-center justify-center gap-2 border border-[#D5A8A4] text-[#5D4050] hover:bg-[#F8EEEE] rounded-full px-8 py-4 text-base transition-all duration-300"
                data-testid="welcome-secondary-cta"
                onClick={() => trackFP("homepage_secondary_cta_clicked")}
              >
                <SparklesIcon className="h-4 w-4" />
                {t("welcome.seeSampleRoutineBtn")}
              </Link>
            </div>

            {showAsGuest && (
              <p
                className="mt-4 text-xs text-[#8A7D82] leading-relaxed max-w-md"
                data-testid="welcome-cta-helper"
              >
                {t("welcome.helperFast")}
              </p>
            )}

            {showAsGuest && (
              <div className="mt-3">
                <button
                  onClick={() => setShowIntro(true)}
                  className="inline-flex items-center gap-1.5 text-xs text-[#9B6A6C] hover:text-[#5D4050] underline-offset-4 hover:underline transition-colors"
                  data-testid="welcome-privacy-intro-link"
                >
                  <Lock className="h-3 w-3" />
                  {t("welcome.privacyIntroLink")}
                </button>
              </div>
            )}

            <div className="mt-8 flex items-center gap-6 text-sm text-[#8A7D82]">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-[#D4B483]" />
                {t("welcome.yourPhotoYourDevice")}
              </div>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-[#B892A4]" />
                {t("welcome.beginnerWelcoming")}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
            className="relative"
            data-testid="welcome-brand-panel"
          >
            <div className="relative rounded-[3rem] overflow-hidden soft-shadow-lg aspect-[4/5]">
              <img
                src={HERO_IMAGE}
                alt={t("welcome.heroImageAlt")}
                className="w-full h-full object-cover object-top"
                data-testid="welcome-hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C2123]/55 via-[#2C2123]/10 to-transparent" />

              {/* Floating value chips — brand pillars at a glance.
                  Built from i18n keys (not raw strings) so the chips
                  re-translate on a Settings → Language switch. The
                  stable English-keyed test ID prevents flaky e2e
                  selectors across locales. */}
              <div
                className="absolute top-6 left-6 right-6 flex flex-wrap gap-2"
                data-testid="welcome-value-chips"
              >
                {[
                  { key: "affirming", label: t("welcome.chipAffirming"), Icon: Heart },
                  { key: "private", label: t("welcome.chipPrivate"), Icon: Lock },
                  { key: "by-for-us", label: t("welcome.chipByForUs"), Icon: SparklesIcon },
                  { key: "every-skin", label: t("welcome.chipEverySkin"), Icon: ScanFace },
                  { key: "every-story", label: t("welcome.chipEveryStory"), Icon: Quote },
                ].map((chip) => (
                  <span
                    key={chip.key}
                    className="inline-flex items-center gap-1.5 bg-white/85 backdrop-blur-md border border-white/70 rounded-full px-2.5 py-1 text-[11px] text-[#2C2123] shadow-sm"
                    data-testid={`welcome-value-chip-${chip.key}`}
                  >
                    <chip.Icon className="h-3 w-3 text-[#9B6A6C]" />
                    {chip.label}
                  </span>
                ))}
              </div>

              {/* Editorial promise card */}
              <div className="absolute bottom-8 left-8 right-8">
                <div className="glass-card rounded-2xl p-5">
                  <Quote className="h-4 w-4 text-[#D5A8A4] mb-2" />
                  <p className="font-serif italic text-xl sm:text-2xl text-[#2C2123] leading-[1.2]">
                    {t("welcome.promiseQuote")}
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#8A7D82] mt-3">
                    {t("welcome.promiseLabel")}
                  </p>
                </div>
              </div>
            </div>

            {/* decorative orbs */}
            <div className="hidden sm:block absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#F2E8E9] blur-2xl -z-10" />
            <div className="hidden sm:block absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-[#D4B483]/40 blur-3xl -z-10" />
          </motion.div>
        </div>
      </section>

      {/* ───────────── MEET TROUVÉ (guests only, post-demo) ─────────────
          The character introduction is intentionally placed AFTER the
          inspiration-photo demo + marketing hero. Founder mandate
          (2026-06-06): "Users need to experience the product before
          they care about the character." */}
      {showAsGuest && <MeetTrouve />}

      {/* ───────────── "RECREATE A CREATOR LOOK" — INLINE NOW ──
          As of Feb 8, 2026 the recreate flow lives ENTIRELY inside
          the homepage consultation. The previous standalone section
          here (HomeInspirationDemo) yanked users out of the chat
          when they tapped "Recreating a look I love" — the founder
          flagged this as a broken primary CTA. The consultation
          chip now opens an inline inspiration upload card directly
          beneath Trouvé's bubble, runs the same /api/inspiration/
          preview analysis, and threads the inspiration brief into
          the selfie analysis via `consultation_focus`. One coherent
          conversation, no scroll-aways. We keep an empty anchor
          here so any deep-link to #recreate-section still scrolls
          to the consultation. */}
      {!user && (
        <div
          id="recreate-section"
          data-testid="recreate-section"
          aria-hidden="true"
        />
      )}

      {/* ───────────── FEATURES ───────────── */}
      <section
        id="features"
        className="px-6 sm:px-10 py-20 sm:py-28"
        data-testid="landing-features"
      >
        <div className="max-w-6xl mx-auto">
          <SectionHeading eyebrow={t("welcome.featuresEyebrow")}>
            {t("welcome.featuresHeadline")}
          </SectionHeading>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                {...fadeUp}
                transition={{
                  ...fadeUp.transition,
                  delay: i * 0.08,
                }}
                className="bg-white/80 backdrop-blur-xl border border-[#EADDD7] rounded-3xl p-8 hover:-translate-y-1 transition-transform duration-500 soft-shadow"
                data-testid={`feature-card-${f.title
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")
                  .replace(/^-|-$/g, "")}`}
              >
                <div className="h-11 w-11 rounded-full bg-[#F2E8E9] flex items-center justify-center mb-5">
                  <f.icon className="h-5 w-5 text-[#9B6A6C]" />
                </div>
                <h3 className="font-serif italic text-2xl text-[#2C2123] leading-tight mb-3">
                  {f.title}
                </h3>
                <p className="text-[#5C4B51] leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TROUVÉ COACHING IN MOTION (Sora 2 hero video) — REMOVED Feb 8 2026.
          The founder pulled all Sora-generated video from the app
          ("all sora videos need to be gone af"). The component file
          and the .mp4/.webm assets have been deleted from the repo.
          Hero footage, if reintroduced, should be real human video. */}

      {/* ───────────── DISCOVER (app screens walkthrough) ───────────── */}
      <DiscoverShowcase />

      {/* ───────────── HOW IT WORKS ───────────── */}
      <section
        id="how"
        className="px-6 sm:px-10 py-20 sm:py-28 bg-[#FBF4EF]"
        data-testid="landing-how"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-12">
            <motion.div {...fadeUp}>
              <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#9B6A6C] mb-3">
                {t("welcome.howEyebrow")}
              </span>
              <h2 className="font-serif italic text-4xl sm:text-5xl leading-tight text-[#2C2123] mb-6">
                {t("welcome.howHeadline")}
              </h2>
              <p className="text-lg text-[#5C4B51] leading-relaxed mb-8">
                {t("welcome.howSubline")}
              </p>
              <Link
                to="/onboarding"
                className="inline-flex items-center gap-2 text-[#5D4050] hover:text-[#9B6A6C] transition-colors group"
                data-testid="landing-how-cta"
              >
                <span className="border-b border-current">
                  {t("welcome.howCtaPrimary")}
                </span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
              {user && (
                <div className="mt-4">
                  <Link
                    to="/recreate"
                    className="inline-flex items-center gap-2 text-[#9B6A6C] hover:text-[#5D4050] transition-colors group text-sm"
                    data-testid="landing-recreate-cta"
                  >
                    <span className="border-b border-current">
                      {t("welcome.howCtaRecreate")}
                    </span>
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </div>
              )}
            </motion.div>

            <div className="space-y-7">
              {steps.map((s) => (
                <motion.div
                  key={s.n}
                  {...fadeUp}
                  className="flex gap-5"
                  data-testid={`landing-step-${s.n}`}
                >
                  <div className="shrink-0 h-10 w-10 rounded-full border border-[#D5A8A4] bg-white flex items-center justify-center font-serif italic text-[#9B6A6C]">
                    {s.n}
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-[#2C2123] mb-1">
                      {s.title}
                    </h4>
                    <p className="text-[#5C4B51] leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── FOUNDER STORY ───────────── */}
      <section
        id="founder"
        className="px-6 sm:px-10 py-20 sm:py-28 bg-[#2C2123] text-white"
        data-testid="landing-founder"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[280px_1fr] gap-10 lg:gap-14 items-start">
          <motion.div
            {...fadeUp}
            className="mx-auto lg:mx-0 lg:sticky lg:top-32"
          >
            <img
              src={HERO_IMAGE}
              alt={t("welcome.founderImageAlt")}
              className="w-56 h-56 rounded-full object-cover object-top border-2 border-[#D5A8A4]"
              data-testid="landing-founder-photo"
            />
            <div className="mt-6 flex flex-col items-center lg:items-start gap-1">
              <div className="font-serif italic text-2xl text-white">
                {FOUNDER_CONTACT.name}
              </div>
              <div className="text-[#D5A8A4] text-sm">{t("founder.role")}</div>
              <a
                href={`mailto:${FOUNDER_CONTACT.contactEmail}`}
                className="inline-flex items-center gap-2 mt-4 text-[#FFE6D7] hover:text-white border-b border-[#FFE6D7]/40 hover:border-white transition-colors text-sm"
                data-testid="landing-founder-email"
              >
                <Mail className="h-3.5 w-3.5" />
                {FOUNDER_CONTACT.contactEmail}
              </a>
            </div>
          </motion.div>

          <motion.div {...fadeUp}>
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#D5A8A4] mb-3">
              {t("founder.eyebrow")}
            </span>
            <h3 className="font-serif italic text-3xl sm:text-4xl leading-tight mb-8">
              {t("founder.headline")}
            </h3>

            <div className="space-y-5 text-white/85 leading-relaxed text-lg max-w-2xl">
              {["founder.bio1", "founder.bio2", "founder.bio3"].map((key, i) => (
                <p
                  key={key}
                  data-testid={`landing-founder-bio-p-${i}`}
                >
                  {t(key)}
                </p>
              ))}
            </div>

            <div
              className="relative mt-10 max-w-2xl pl-6 border-l-2 border-[#D5A8A4]"
              data-testid="landing-founder-pullquote"
            >
              <Quote className="h-5 w-5 text-[#D5A8A4] mb-3" />
              <p className="font-serif italic text-2xl sm:text-3xl text-white leading-snug">
                {t("founder.pullQuote")}
              </p>
            </div>

            <div className="mt-8 grid sm:grid-cols-3 gap-3 max-w-2xl">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-start gap-3">
                <ShieldCheck className="h-4 w-4 text-[#D5A8A4] mt-1 shrink-0" />
                <div className="text-sm text-white/85 leading-relaxed">
                  {t("founder.credentialTrust")}
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-start gap-3">
                <Heart className="h-4 w-4 text-[#D5A8A4] mt-1 shrink-0" />
                <div className="text-sm text-white/85 leading-relaxed">
                  {t("founder.credentialDrag")}
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-start gap-3">
                <Eye className="h-4 w-4 text-[#D5A8A4] mt-1 shrink-0" />
                <div className="text-sm text-white/85 leading-relaxed">
                  {t("founder.credentialAi")}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────────── PRICING ───────────── */}
      {!readerApp && (
      <section
        id="pricing"
        className="px-6 sm:px-10 py-20 sm:py-28"
        data-testid="landing-pricing"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#9B6A6C] mb-3">
              {t("welcome.pricingEyebrow")}
            </span>
            <h2 className="font-serif italic text-4xl sm:text-5xl leading-tight text-[#2C2123] mb-4">
              {t("welcome.pricingHeadline")}
            </h2>
            <p className="text-lg text-[#5C4B51] leading-relaxed max-w-xl mx-auto">
              {t("welcome.pricingBody")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {PLANS.map((plan) => (
              <motion.div
                key={plan.name}
                {...fadeUp}
                className={
                  plan.highlight
                    ? "bg-[#2C2123] text-white rounded-3xl p-8 soft-shadow-lg relative"
                    : "bg-white border border-[#EADDD7] rounded-3xl p-8 soft-shadow"
                }
                data-testid={plan.testid}
              >
                {plan.highlight ? (
                  <span className="absolute -top-3 left-8 bg-[#D5A8A4] text-[#2C2123] text-xs uppercase tracking-[0.18em] px-3 py-1 rounded-full">
                    {t("welcome.mostPicked")}
                  </span>
                ) : null}
                <div
                  className={
                    plan.highlight
                      ? "text-[#D5A8A4] text-sm uppercase tracking-[0.18em] mb-2"
                      : "text-[#9B6A6C] text-sm uppercase tracking-[0.18em] mb-2"
                  }
                >
                  {plan.name}
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span
                    className={
                      plan.highlight
                        ? "text-5xl font-light text-white"
                        : "text-5xl font-light text-[#2C2123]"
                    }
                  >
                    {plan.price}
                  </span>
                  <span
                    className={
                      plan.highlight
                        ? "text-white/70 ml-1"
                        : "text-[#8A7D82] ml-1"
                    }
                  >
                    / {plan.cadence}
                  </span>
                </div>
                <p
                  className={
                    plan.highlight
                      ? "text-white/80 mb-6"
                      : "text-[#5C4B51] mb-6"
                  }
                >
                  {plan.tagline}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li
                      key={f}
                      className={
                        plan.highlight
                          ? "flex items-start gap-2 text-white/90"
                          : "flex items-start gap-2 text-[#2C2123]"
                      }
                    >
                      <Check
                        className={
                          plan.highlight
                            ? "h-4 w-4 text-[#D5A8A4] mt-1 shrink-0"
                            : "h-4 w-4 text-[#9B6A6C] mt-1 shrink-0"
                        }
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan.cta.to}
                  className={
                    plan.highlight
                      ? "inline-flex items-center justify-center gap-2 w-full bg-[#D5A8A4] text-[#2C2123] hover:bg-[#E5BFBC] rounded-full px-6 py-3.5 text-base transition-all duration-300"
                      : "inline-flex items-center justify-center gap-2 w-full bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-6 py-3.5 text-base transition-all duration-300"
                  }
                  data-testid={`${plan.testid}-cta`}
                >
                  {plan.cta.label}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </motion.div>
            ))}
          </div>

          <p className="text-center mt-8 text-sm text-[#8A7D82]">
            {t("welcome.pricingFullLinkPrefix")}{" "}
            <Link
              to="/pricing"
              className="text-[#9B6A6C] underline-offset-4 hover:underline"
              data-testid="landing-pricing-full-link"
            >
              {t("welcome.pricingFullLinkLabel")}
            </Link>
            .
          </p>
        </div>
      </section>
      )}

      {/* ───────────── INVESTOR / CO-FOUNDER CONNECT ───────────── */}
      {!readerApp && (
      <section
        id="connect"
        className="px-6 sm:px-10 py-20 sm:py-28 bg-[#FBF4EF] border-t border-[#EADDD7]"
        data-testid="landing-connect"
      >
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#9B6A6C] mb-3">
              {t("connect.eyebrow")}
            </span>
            <h2 className="font-serif italic text-4xl sm:text-5xl leading-tight text-[#2C2123] mb-4">
              {t("connect.headline")}
            </h2>
            <p className="text-lg text-[#5C4B51] leading-relaxed max-w-2xl mx-auto">
              {t("connect.body")}
            </p>
          </div>

          <ConnectForm />

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4 text-sm">
            <a
              href={FOUNDER_CONTACT.calendly}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#5D4050] hover:text-[#9B6A6C] transition-colors"
              data-testid="landing-calendly-link"
            >
              <Calendar className="h-4 w-4" />
              {t("connect.calendlyLabel")}
            </a>
            <span className="text-[#BBA7AD]">·</span>
            <Link
              to="/pitch"
              className="inline-flex items-center gap-2 text-[#5D4050] hover:text-[#9B6A6C] transition-colors"
              data-testid="landing-pitch-link"
            >
              {t("connect.pitchLabel")}
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* ───────────── FINAL REASSURANCE ───────────── */}
      <section className="pb-32 px-6 sm:px-10 pt-20">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-serif italic text-3xl sm:text-4xl text-[#5D4050] leading-tight">
            {t("welcome.finalReassureBefore")}{" "}
            <span className="text-[#9B6A6C]">
              {t("welcome.finalReassureEmphasis")}
            </span>
            <br />
            {t("welcome.finalReassureAfter")}
          </p>
          <div className="mt-10">
            <Link
              to={primaryCtaTo}
              onClick={handlePrimaryCta}
              className="group inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-8 py-4 text-base transition-all duration-300 shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5"
              data-testid="welcome-launch-app-footer-btn"
            >
              {primaryCtaLabel}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <PrivacyIntro
        forceOpen={showIntro}
        onClose={handlePrivacyIntroClose}
      />
    </div>
  );
};

/**
 * SignedInLaunchpad — 4-card action board for authenticated users.
 * Renders right above the marketing hero on `/` so the very first
 * surface a returning user sees is a launchpad to their daily ritual,
 * not the elevator pitch they've already heard.
 *
 * The four cards are ranked by intent strength (left → right, top → bottom
 * on mobile):
 *   1. Selfie scan — the canonical "I want a routine for my face today"
 *      flow. This is the highest-intent action and gets the brightest
 *      card (filled plum gradient).
 *   2. Recreate a look — the "I saw this on TikTok and want to try it"
 *      flow. Second-highest intent because the user already knows what
 *      they want and just needs the AR mirror to walk them through it.
 *      Bordered/cream so it complements the primary CTA without
 *      competing for visual hierarchy.
 *   3. Techniques — the "I want to learn smoky eye real quick" flow.
 *      Pro-gated, but the card is visible to free users with a clear
 *      "Try the library free for 7 days" subtitle.
 *   4. My routines — the "open something I already made" return path.
 *      Quietest card — important but only relevant for users who have
 *      saved routines.
 */
const SignedInLaunchpad = ({ userName }) => {
  return (
    <section
      className="relative pt-28 pb-6 px-6 sm:px-10"
      data-testid="signed-in-launchpad"
    >
      <div className="max-w-6xl mx-auto">
        {/* Personal greeting — light touch, sets tone vs. cold hero. */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8 max-w-2xl"
        >
          <p className="text-[10px] uppercase tracking-[0.22em] text-[#9B6A6C] mb-2">
            Welcome back
          </p>
          <h2 className="font-serif italic text-3xl sm:text-4xl text-[#2C2123] leading-[1.1]">
            {userName ? `Hi ${userName.split(" ")[0]} —` : "Hi there —"} what would
            you like to do today?
          </h2>
        </motion.div>

        {/* 2x2 grid on desktop, single column on mobile. Cards 1+2 get
            equal visual weight because they're the two most common entry
            points; 3+4 sit slightly quieter underneath. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <LaunchpadCard
            to="/capture"
            tier="primary"
            icon={<Camera className="h-5 w-5" />}
            kicker="One-tap routine"
            title="Take a selfie"
            body="Trouvé reads your features and writes you a routine that celebrates them."
            cta="Start a scan"
            testId="launchpad-capture"
          />
          <LaunchpadCard
            to="/recreate"
            tier="secondary"
            icon={<Wand2 className="h-5 w-5" />}
            kicker="Adapt any tutorial"
            title="Recreate a look"
            body="Paste a YouTube, TikTok, or Instagram tutorial — or describe one — and Trouvé adapts it to your face."
            cta="Try a tutorial"
            testId="launchpad-recreate"
          />
          <LaunchpadCard
            to="/techniques"
            tier="secondary"
            icon={<BookOpen className="h-5 w-5" />}
            kicker="Skill library · Pro"
            title="Learn a technique"
            body="Twelve step-by-step mini-tutorials — smoky eye, cut crease, contour, snatched brow. No face scan needed."
            cta="Browse techniques"
            testId="launchpad-techniques"
          />
          <LaunchpadCard
            to="/my-routines"
            tier="quiet"
            icon={<Bookmark className="h-5 w-5" />}
            kicker="Your saves"
            title="My routines"
            body="Pick up where you left off — your most recent routine is always free; full history with Pro."
            cta="Open library"
            testId="launchpad-library"
          />
        </div>
      </div>
    </section>
  );
};

/**
 * LaunchpadCard — three visual tiers so the action board has clear
 * hierarchy without all four cards competing for attention.
 *   - primary: filled plum gradient, white text. ONE per board.
 *   - secondary: cream-tinted with plum border accent. The "also try"
 *     options.
 *   - quiet: white card with subtle border. Important but background.
 */
const LaunchpadCard = ({ to, tier, icon, kicker, title, body, cta, testId }) => {
  const styles = {
    primary: {
      card:
        "bg-gradient-to-br from-[#9B6A6C] to-[#805557] text-white shadow-[0_12px_36px_rgba(155,106,108,0.32)] hover:shadow-[0_16px_44px_rgba(155,106,108,0.42)] border border-transparent",
      kicker: "text-[#F8EEEE]/85",
      iconWrap: "bg-white/20 text-white",
      body: "text-white/85",
      cta: "text-white",
    },
    secondary: {
      card:
        "bg-white border border-[#EADDD7] hover:border-[#D5A8A4] hover:shadow-[0_8px_24px_rgba(155,106,108,0.12)]",
      kicker: "text-[#9B6A6C]",
      iconWrap: "bg-[#F8EEEE] text-[#9B6A6C]",
      body: "text-[#5C4B51]",
      cta: "text-[#9B6A6C]",
    },
    quiet: {
      card:
        "bg-[#FDFBF7] border border-[#EADDD7] hover:border-[#D5A8A4] hover:shadow-[0_6px_20px_rgba(155,106,108,0.08)]",
      kicker: "text-[#8A7D82]",
      iconWrap: "bg-white text-[#9B6A6C] border border-[#EADDD7]",
      body: "text-[#5C4B51]",
      cta: "text-[#9B6A6C]",
    },
  }[tier];

  return (
    <Link
      to={to}
      className={`group relative rounded-2xl p-6 sm:p-7 transition-all duration-300 hover:-translate-y-0.5 ${styles.card}`}
      data-testid={testId}
    >
      <div className="flex items-start gap-4">
        <div
          className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${styles.iconWrap}`}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={`text-[10px] uppercase tracking-[0.18em] mb-1.5 ${styles.kicker}`}
          >
            {kicker}
          </p>
          <h3 className="font-serif italic text-2xl mb-2 leading-tight">
            {title}
          </h3>
          <p className={`text-sm leading-relaxed mb-4 ${styles.body}`}>
            {body}
          </p>
          <span
            className={`inline-flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all ${styles.cta}`}
          >
            {cta}
            <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default Welcome;

// ─────────────────────────────────────────────────────────────
// GuestCaptureFirstLanding — Feb 2026 capture-first redesign
// ─────────────────────────────────────────────────────────────
// Purpose-built landing for unauthenticated visitors. Every visible
// top-level element drives one of three things: free signup, signup
// friction reduction, or reinforcement of account value. Everything
// else lives behind the overflow menu.
//
// Page structure:
//   1. Slim header — logo + primary CTA + overflow (hamburger)
//   2. Hero — headline, subline, CTA, privacy line, trust strip
//   3. Brand promise — "digital drag mom" positioning
//   4. How it begins — honest 4-step that reflects the real guest flow
//   5. AR mirror — centerpiece value section with generated hero image
//   6. Try-it-first → save-it value section (the explicit gate framing)
//   7. Privacy + safety — six trust bullets, calm tone
//   8. Pricing — free is the headline, Pro stays subdued
//   9. Founder short-bio — lower in the funnel
//  10. Final reassurance + footer
//
// The hero now leads with the founder portrait (Iyal v0-original)
// — identity-first, affirming, real. The two MIRROR-MODE product
// screenshots have moved into the AR feature section deeper down
// the page, where they're displayed as a composition (landscape
// background + floating portrait phone-frame) so both mirror
// surfaces stay visible without competing with the founder image.
const HERO_PORTRAIT = "/img/iyal-hero-v0-original.jpg";
// Trouvé character illustrations. We use TWO different assets:
//   • TROUVE_HERO         — full-body single-subject portrait, used on
//                           desktop's right column where there's room
//                           to showcase the character with the warm
//                           painterly background.
//   • TROUVE_PORTRAIT     — head-and-chest portrait (iter 82, Feb 27
//                           2026). Used on mobile where we want the
//                           character to greet the user like a beauty
//                           consultant — not a banner, not a tiny
//                           avatar. Rendered ~280px tall with a soft
//                           radial mask so the rectangular PNG edges
//                           dissolve into the page gradient, and a
//                           cream halo behind her for warmth.
// Neither asset ships with a transparent background, but the
// portrait-avatar's soft beige gradient is soft enough that a
// `mask-image: radial-gradient(...)` makes it sit cleanly over the
// page without hard edges.
const TROUVE_HERO = "/trouve/hero.png";
const TROUVE_PORTRAIT = "/trouve/portrait-avatar.png";
const MIRROR_FEATURE_LANDSCAPE = "/img/mirror-canvas-landscape.jpg";
const MIRROR_FEATURE_PORTRAIT = "/img/mirror-mode-portrait.jpg";

// Original, code-native celebration burst. The particles are deliberately
// deterministic so every visitor gets the same polished one-shot animation
// without loading or licensing a third-party motion asset.
const CELEBRATION_PARTICLES = [
  { x: -176, y: -92, rotate: -280, color: "#E7A84B", w: 9, h: 9, round: true, delay: 0.00 },
  { x: -150, y: -128, rotate: -220, color: "#D97891", w: 5, h: 18, delay: 0.04 },
  { x: -122, y: -72, rotate: -310, color: "#6CA6A2", w: 11, h: 7, delay: 0.10 },
  { x: -98, y: -150, rotate: -190, color: "#F4DCD3", w: 8, h: 8, round: true, delay: 0.02 },
  { x: -72, y: -108, rotate: -250, color: "#B48CC8", w: 6, h: 16, delay: 0.14 },
  { x: -42, y: -164, rotate: -180, color: "#F2C75C", w: 10, h: 6, delay: 0.08 },
  { x: -18, y: -116, rotate: -300, color: "#E98C70", w: 8, h: 8, round: true, delay: 0.16 },
  { x: 18, y: -154, rotate: 220, color: "#6CA6A2", w: 5, h: 18, delay: 0.05 },
  { x: 48, y: -112, rotate: 300, color: "#D97891", w: 12, h: 7, delay: 0.12 },
  { x: 82, y: -148, rotate: 190, color: "#E7A84B", w: 8, h: 8, round: true, delay: 0.03 },
  { x: 112, y: -102, rotate: 260, color: "#B48CC8", w: 6, h: 17, delay: 0.15 },
  { x: 146, y: -126, rotate: 230, color: "#F4DCD3", w: 11, h: 6, delay: 0.07 },
  { x: 180, y: -78, rotate: 320, color: "#E98C70", w: 9, h: 9, round: true, delay: 0.11 },
  { x: -194, y: -18, rotate: -340, color: "#6CA6A2", w: 6, h: 18, delay: 0.06 },
  { x: -164, y: 32, rotate: -240, color: "#F2C75C", w: 12, h: 7, delay: 0.17 },
  { x: -136, y: 78, rotate: -300, color: "#D97891", w: 8, h: 8, round: true, delay: 0.09 },
  { x: -98, y: 110, rotate: -210, color: "#B48CC8", w: 5, h: 17, delay: 0.18 },
  { x: -52, y: 132, rotate: -270, color: "#E7A84B", w: 11, h: 6, delay: 0.13 },
  { x: -14, y: 98, rotate: -190, color: "#F4DCD3", w: 9, h: 9, round: true, delay: 0.04 },
  { x: 28, y: 126, rotate: 250, color: "#E98C70", w: 6, h: 18, delay: 0.16 },
  { x: 68, y: 104, rotate: 310, color: "#6CA6A2", w: 12, h: 7, delay: 0.08 },
  { x: 108, y: 126, rotate: 210, color: "#F2C75C", w: 8, h: 8, round: true, delay: 0.14 },
  { x: 146, y: 70, rotate: 290, color: "#D97891", w: 5, h: 17, delay: 0.02 },
  { x: 182, y: 28, rotate: 230, color: "#B48CC8", w: 11, h: 6, delay: 0.12 },
  { x: 198, y: -24, rotate: 330, color: "#E7A84B", w: 9, h: 9, round: true, delay: 0.06 },
  { x: -116, y: 18, rotate: -260, color: "#E98C70", w: 6, h: 15, delay: 0.15 },
  { x: 124, y: 12, rotate: 280, color: "#F4DCD3", w: 6, h: 16, delay: 0.10 },
  { x: 4, y: -92, rotate: 200, color: "#D97891", w: 10, h: 10, round: true, delay: 0.18 },
];

const GuestCaptureFirstLanding = ({
  onPrimaryCta,
  primaryCtaTo,
  onOpenPrivacyIntro,
  showIntro,
  onCloseIntro,
  readerApp,
}) => {
  const { t } = useTranslation();
  const isConfettiPreview =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("confetti");

  // The trust strip is the only place we render 5 inline chips —
  // built from i18n keys so the strip re-translates on Settings →
  // Language without a re-mount.
  const trustChips = [
    { key: "free", label: t("captureFirst.trustFree") },
    { key: "no-card", label: t("captureFirst.trustNoCard") },
    { key: "one-minute", label: t("captureFirst.trustOneMinute") },
    { key: "beginner", label: t("captureFirst.trustBeginner") },
    { key: "affirming", label: t("captureFirst.trustAffirming") },
  ];

  // How-it-begins is intentionally a 4-step recap, NOT a product tour.
  // It mirrors the real guest journey so the page doesn't lie about
  // what happens before signup.
  const howSteps = [
    { n: "1", title: t("captureFirst.howStep1Title"), body: t("captureFirst.howStep1Body"), Icon: SparklesIcon },
    { n: "2", title: t("captureFirst.howStep2Title"), body: t("captureFirst.howStep2Body"), Icon: Camera },
    { n: "3", title: t("captureFirst.howStep3Title"), body: t("captureFirst.howStep3Body"), Icon: ScanFace },
    { n: "4", title: t("captureFirst.howStep4Title"), body: t("captureFirst.howStep4Body"), Icon: Bookmark },
  ];

  const privacyPoints = [
    t("captureFirst.privacyPoint1"),
    t("captureFirst.privacyPoint2"),
    t("captureFirst.privacyPoint3"),
    t("captureFirst.privacyPoint4"),
    t("captureFirst.privacyPoint5"),
    t("captureFirst.privacyPoint6"),
  ];

  return (
    <div className="relative min-h-screen trubeaute-gradient">
      <WaitlistBanner />
      <CaptureFirstHeader readerApp={readerApp} />

      {/* ───────────── HERO ─────────────
          Asymmetric grid: copy left (40%), AR mirror right (60%) on
          desktop. The AR mirror image is the dominant visual proof —
          replaces the old static routine diagrams.

          Iter 80 (Feb 2026) mobile-first overhaul: a Trouvé character
          illustration is mounted directly under the nav as the mobile
          centerpiece, with the chat bubble pulled up via a negative
          margin so Trouvé "speaks" the opener. The marketing H1 stays
          on desktop but is hidden on mobile so the illustration + chat
          fit above the fold. The desktop right-column image is also
          swapped to the Trouvé illustration so the brand mascot anchors
          the hero on every viewport. */}
      <section
        className="relative overflow-x-hidden pt-24 sm:pt-28 pb-16 sm:pb-24 px-6 sm:px-10"
        data-testid="welcome-hero"
      >
        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.7, rotate: -10 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.45, duration: 0.55, ease: "easeOut" }}
            className="pointer-events-none absolute right-0 top-0 z-30 sm:right-2 lg:right-8"
            aria-label={`${t("captureFirst.prizeEvent")}: ${t("captureFirst.prizeTop")} ${t("captureFirst.prizeLabel")}`}
            data-award-sticker="true"
            data-testid="top-10-prize-sticker"
          >
            {CELEBRATION_PARTICLES.map((particle, index) => {
              // Keep a true radial burst around the upper-right sticker while
              // shortening the top/right trajectories enough to stay on-screen.
              const destinationX =
                particle.x > 0 ? Math.min(particle.x * 0.25, 46) : particle.x;
              const destinationY =
                particle.y < 0
                  ? -Math.min(Math.abs(particle.y) * 0.5 + 18, 92)
                  : Math.min(particle.y * 0.85 + 36, 150);

              return (
                <motion.span
                key={`${particle.x}-${particle.y}`}
                initial={{ opacity: 0, x: 0, y: 0, scale: 0.2, rotate: 0 }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  x: [0, destinationX * 0.28, destinationX * 0.75, destinationX],
                  y: [0, destinationY * 0.2, destinationY * 0.72, destinationY],
                  scale: [0.2, 1.35, 1, 0.7],
                  rotate: [0, particle.rotate * 0.28, particle.rotate * 0.72, particle.rotate],
                }}
                transition={{
                  delay: 0.42 + particle.delay,
                  duration: isConfettiPreview ? 1.7 : 1.55 + (index % 4) * 0.08,
                  ease: "easeOut",
                  times: [0, 0.14, 0.72, 1],
                  repeat: isConfettiPreview ? Infinity : 0,
                  repeatDelay: isConfettiPreview ? 1.35 : 0,
                }}
                className="absolute left-1/2 top-1/2"
                style={{
                  left: particle.x > 0 ? "100%" : "50%",
                  width: particle.w * 1.25,
                  height: particle.h * 1.25,
                  borderRadius: particle.round ? "999px" : "2px",
                  backgroundColor: particle.color,
                  boxShadow: "0 2px 5px rgba(44, 33, 35, 0.18)",
                }}
                aria-hidden="true"
                data-confetti-particle="true"
                />
              );
            })}

            <div className="inline-flex rotate-3 items-center gap-2.5 rounded-2xl border-2 border-[#D5A8A4] bg-[#2C2123] px-4 py-2.5 text-left text-white shadow-[0_14px_32px_-14px_rgba(44,33,35,0.8)]">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F4DCD3] text-[#5D4050]">
                <span className="text-lg" aria-hidden="true">
                  ★
                </span>
              </span>
              <span className="leading-none">
                <span className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-[#F4DCD3]">
                  {t("captureFirst.prizeEvent")}
                </span>
                <span className="mt-1.5 block text-center text-sm font-semibold tracking-wide text-white">
                  {t("captureFirst.prizeTop")}
                </span>
                <span className="mt-1 block text-center text-xs font-medium tracking-wide text-white/85">
                  {t("captureFirst.prizeLabel")}
                </span>
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            {/* Mobile-only Trouvé portrait greeting.
                Iter 82 (Feb 27 2026) — the founder's mobile mockup
                shows Trouvé as a LARGE portrait (~280px tall, face +
                hair + chest visible) sitting in soft cream space, NOT
                a circular avatar and NOT a rectangular banner. The
                rectangular PNG edges are dissolved with a radial-
                gradient mask so she "blooms" into the page; a soft
                cream halo sits behind her head for warmth. Chat card
                below uses `-mt-10` so the bubble pulls into the
                bottom of the portrait — she's literally speaking the
                greeting. */}
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="lg:hidden flex justify-center mt-24"
              data-testid="welcome-trouve-mobile-illustration-wrap"
            >
              <div className="relative w-full max-w-[320px] flex justify-center">
                {/* Soft cream halo behind Trouvé — anchored top so
                    it sits behind her head/shoulders, not the chest.
                    The blur + low opacity keep it gentle. */}
                <div
                  aria-hidden="true"
                  className="absolute top-2 left-1/2 -translate-x-1/2 w-[260px] h-[260px] rounded-full bg-gradient-to-br from-[#FFF8F5] via-[#F8EEEE] to-[#F2E8E9] blur-xl opacity-80 -z-10"
                />
                <img
                  src={TROUVE_PORTRAIT}
                  alt={t("captureFirst.heroImageAlt")}
                  className="h-[280px] w-auto select-none pointer-events-none"
                  draggable={false}
                  data-testid="welcome-trouve-mobile-illustration"
                  style={{
                    maskImage:
                      "radial-gradient(ellipse 70% 80% at 50% 45%, black 55%, transparent 92%)",
                    WebkitMaskImage:
                      "radial-gradient(ellipse 70% 80% at 50% 45%, black 55%, transparent 92%)",
                  }}
                />
              </div>
            </motion.div>

            <h1
              className="hidden lg:block font-serif text-5xl sm:text-6xl lg:text-[4.5rem] text-[#2C2123] leading-[1.02] tracking-tight mb-6"
              data-testid="welcome-hero-headline"
            >
              <span className="italic text-[#5D4050]">
                {t("captureFirst.heroHeadline")}
              </span>
            </h1>

            {/* Hero Trouvé Chat — iter 79 (Jun 25 2026). Replaces the
                marketing-only primary CTA with a 2-turn chat that
                captures intent before signup. The secondary "skip chat
                and start your free routine" link lives INSIDE the
                component (passed via onSkip) so the founder's original
                CTA path is preserved — just demoted from the loudest
                button to a soft text link below the chat. The H1 +
                subline above stay for SEO and brand voice — the chat
                is the action, the copy is the promise.

                Iter 80: on mobile the chat is pulled up with `-mt-6`
                so the bubble overlaps the bottom of the Trouvé
                illustration above it. `relative` + z-index keeps the
                rounded chat card on top of the illustration. */}
            <div
              className="mb-5 -mt-10 lg:mt-0 relative z-10"
              data-testid="welcome-hero-chat-wrap"
            >
              <HeroTrouveChat onSkip={onPrimaryCta} />
            </div>

            <p
              className="text-xs text-[#8A7D82] leading-relaxed max-w-md mb-6 flex items-start gap-2 justify-center text-center mx-auto lg:justify-start lg:text-left lg:mx-0"
              data-testid="welcome-privacy-line"
            >
              <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#9B6A6C]" />
              <span>{t("captureFirst.heroPrivacyLine")}</span>
            </p>

            {/* Trust strip + privacy intro link — desktop only.
                Iter 82 mobile redesign keeps the above-the-fold space
                clean per the founder's mockup: nav → portrait → chat
                → chips → input → privacy line, and nothing else. The
                trust strip + "How we keep your selfie private" link
                still render for desktop visitors who have the screen
                real estate. */}
            <div
              className="hidden lg:flex flex-wrap gap-x-4 gap-y-2 text-xs text-[#5C4B51]"
              data-testid="welcome-trust-strip"
            >
              {trustChips.map((chip, i) => (
                <React.Fragment key={chip.key}>
                  {i > 0 && (
                    <span className="text-[#D5A8A4]" aria-hidden="true">·</span>
                  )}
                  <span
                    className="inline-flex items-center gap-1"
                    data-testid={`welcome-trust-chip-${chip.key}`}
                  >
                    <Check className="h-3 w-3 text-[#9B6A6C]" />
                    {chip.label}
                  </span>
                </React.Fragment>
              ))}
            </div>

            <button
              type="button"
              onClick={onOpenPrivacyIntro}
              className="mt-5 hidden lg:inline-flex items-center gap-1.5 text-xs text-[#9B6A6C] hover:text-[#5D4050] underline-offset-4 hover:underline transition-colors"
              data-testid="welcome-privacy-intro-link"
            >
              <Lock className="h-3 w-3" />
              {t("welcome.privacyIntroLink")}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
            className="relative hidden lg:block"
            data-testid="welcome-hero-image-wrap"
          >
            {/* Desktop right column — Trouvé illustration anchored to
                a soft cream gradient. We use `object-contain` so the
                character isn't cropped, and a light gradient instead
                of the previous dark `#1f1517` because the Trouvé PNG
                reads best on warm cream tones (matches the rest of
                the trubeaute palette). The mobile equivalent of this
                surface lives above (lg:hidden) — keeping both gives
                us a coherent brand visual on every viewport without
                the mobile image being a tiny afterthought. */}
            <div className="relative mx-auto max-w-sm sm:max-w-md lg:max-w-none rounded-[2.5rem] overflow-hidden soft-shadow-lg aspect-[3/4] bg-gradient-to-br from-[#FBF4EF] via-[#F8EEEE] to-[#F2E8E9]">
              <img
                src={TROUVE_HERO}
                alt={t("captureFirst.heroImageAlt")}
                className="w-full h-full object-contain p-6"
                data-testid="welcome-ar-hero-image"
              />
            </div>
            <div className="hidden sm:block absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#F2E8E9] blur-2xl -z-10" />
            <div className="hidden sm:block absolute -bottom-8 -right-8 w-36 h-36 rounded-full bg-[#D4B483]/40 blur-3xl -z-10" />
          </motion.div>
        </div>
      </section>

      {/* ───────────── BRAND PROMISE ───────────── */}
      <section
        className="px-6 sm:px-10 py-16 sm:py-20 bg-[#FBF4EF] border-y border-[#EADDD7]"
        data-testid="landing-promise"
      >
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block text-[11px] uppercase tracking-[0.3em] text-[#9B6A6C] mb-3">
            {t("captureFirst.promiseEyebrow")}
          </span>
          <h2 className="font-serif italic text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#2C2123] mb-6">
            {t("captureFirst.promiseHeadline")}
          </h2>
          <p className="text-lg text-[#5C4B51] leading-relaxed">
            {t("captureFirst.promiseBody")}
          </p>
        </div>
      </section>

      {/* ───────────── HOW IT BEGINS ───────────── */}
      <section
        id="how"
        className="px-6 sm:px-10 py-20 sm:py-28"
        data-testid="landing-how"
      >
        <div className="max-w-6xl mx-auto">
          <div className="mb-12 max-w-2xl">
            <span className="inline-block text-[11px] uppercase tracking-[0.3em] text-[#9B6A6C] mb-3">
              {t("captureFirst.howEyebrow")}
            </span>
            <h2 className="font-serif italic text-4xl sm:text-5xl leading-tight text-[#2C2123]">
              {t("captureFirst.howHeadline")}
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {howSteps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08, ease: "easeOut" }}
                className="bg-white border border-[#EADDD7] rounded-3xl p-6 hover:-translate-y-0.5 hover:border-[#D5A8A4] transition-all duration-300 soft-shadow"
                data-testid={`landing-step-${s.n}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-full bg-[#F8EEEE] flex items-center justify-center">
                    <s.Icon className="h-4 w-4 text-[#9B6A6C]" />
                  </div>
                  <span className="font-serif italic text-2xl text-[#9B6A6C]">
                    {s.n}
                  </span>
                </div>
                <h3 className="font-medium text-[#2C2123] mb-2 leading-tight">
                  {s.title}
                </h3>
                <p className="text-sm text-[#5C4B51] leading-relaxed">
                  {s.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────── AR MIRROR FEATURE (centerpiece) ───────────── */}
      <section
        id="ar-mirror"
        className="px-6 sm:px-10 py-20 sm:py-28 bg-gradient-to-br from-[#FBF4EF] via-[#F8EEEE] to-[#F2E8E9]"
        data-testid="landing-ar-mirror"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[7fr_5fr] gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="relative"
          >
            {/* Two-image composition. The landscape "Smooth the
                canvas" face-tracking shot is the dominant frame
                (16:10 rounded rectangle). A smaller portrait
                "MIRROR MODE · HAIRLINE · Contour for long shape"
                shot floats over the bottom-left corner in its own
                phone-frame, anchored object-top so the MIRROR MODE
                branding stays legible. Together they tell the
                "different stages of the same private mirror" story
                without forcing the user to swipe a carousel. */}
            <div
              className="relative rounded-[2.5rem] overflow-hidden soft-shadow-lg aspect-[16/10]"
              data-testid="landing-ar-mirror-image-wrap"
            >
              <img
                src={MIRROR_FEATURE_LANDSCAPE}
                alt={t("captureFirst.arImageAlt")}
                className="w-full h-full object-cover"
                data-testid="landing-ar-mirror-image"
              />
            </div>
            {/* Floating portrait phone-frame. Positioned bottom-left,
                pulled outside the parent box so it reads as "a
                second moment from the same product" rather than an
                inset. On mobile the absolute positioning gracefully
                collapses (we keep it visible but smaller). */}
            <motion.div
              initial={{ opacity: 0, y: 16, rotate: -2 }}
              whileInView={{ opacity: 1, y: 0, rotate: -2 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
              className="absolute -bottom-6 -left-4 sm:-bottom-10 sm:-left-8 w-28 sm:w-36 lg:w-44 aspect-[3/4] rounded-2xl overflow-hidden bg-[#1f1517] soft-shadow-lg ring-4 ring-white"
              data-testid="landing-ar-mirror-inset-wrap"
            >
              <img
                src={MIRROR_FEATURE_PORTRAIT}
                alt={t("captureFirst.arImagePortraitAlt")}
                className="w-full h-full object-cover object-top"
                data-testid="landing-ar-mirror-inset"
              />
            </motion.div>
          </motion.div>
          <div>
            <span className="inline-block text-[11px] uppercase tracking-[0.3em] text-[#9B6A6C] mb-3">
              {t("captureFirst.arEyebrow")}
            </span>
            <h2 className="font-serif italic text-4xl sm:text-5xl leading-tight text-[#2C2123] mb-6">
              {t("captureFirst.arHeadline")}
            </h2>
            <p className="text-lg text-[#5C4B51] leading-relaxed mb-6">
              {t("captureFirst.arBody")}
            </p>
            <ul className="space-y-2.5 mb-8">
              {[
                t("captureFirst.arSupport1"),
                t("captureFirst.arSupport2"),
                t("captureFirst.arSupport3"),
              ].map((line) => (
                <li
                  key={line}
                  className="flex items-start gap-2 text-[#2C2123]"
                >
                  <SparklesIcon className="h-4 w-4 text-[#9B6A6C] mt-1 shrink-0" />
                  <span className="leading-relaxed">{line}</span>
                </li>
              ))}
            </ul>
            <Link
              to={primaryCtaTo}
              onClick={onPrimaryCta}
              className="group inline-flex items-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-7 py-3.5 text-base font-medium transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5"
              data-testid="landing-ar-cta"
            >
              {t("captureFirst.arCta")}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ───────────── TRY-FIRST → SAVE-LATER VALUE SECTION ───────────── */}
      <section
        className="px-6 sm:px-10 py-20 sm:py-28"
        data-testid="landing-value-frame"
      >
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif italic text-4xl sm:text-5xl leading-tight text-[#2C2123] mb-6">
            {t("captureFirst.valueHeadline")}
          </h2>
          <p className="text-lg text-[#5C4B51] leading-relaxed mb-10 max-w-2xl mx-auto">
            {t("captureFirst.valueBody")}
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mb-10 text-left">
            {[
              { Icon: Bookmark, label: t("captureFirst.valueBullet1"), key: "save" },
              { Icon: SparklesIcon, label: t("captureFirst.valueBullet2"), key: "tailored" },
              { Icon: RotateCcw, label: t("captureFirst.valueBullet3"), key: "return" },
            ].map((b) => (
              <div
                key={b.key}
                className="flex items-start gap-3 bg-white border border-[#EADDD7] rounded-2xl p-5 hover:border-[#D5A8A4] transition-colors"
                data-testid={`landing-value-bullet-${b.key}`}
              >
                <span className="h-9 w-9 rounded-full bg-[#F8EEEE] flex items-center justify-center shrink-0">
                  <b.Icon className="h-4 w-4 text-[#9B6A6C]" />
                </span>
                <span className="text-sm text-[#2C2123] leading-relaxed pt-1.5">
                  {b.label}
                </span>
              </div>
            ))}
          </div>
          <Link
            to={primaryCtaTo}
            onClick={onPrimaryCta}
            className="group inline-flex items-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-7 py-3.5 text-base font-medium transition-all shadow-[0_6px_20px_rgba(155,106,108,0.35)] hover:-translate-y-0.5"
            data-testid="landing-value-cta"
          >
            {t("captureFirst.heroPrimaryCta")}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      {/* ───────────── PRIVACY + SAFETY ───────────── */}
      <section
        className="px-6 sm:px-10 py-20 sm:py-28 bg-[#2C2123] text-white"
        data-testid="landing-privacy"
      >
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[5fr_7fr] gap-12 lg:gap-16">
          <div>
            <span className="inline-block text-[11px] uppercase tracking-[0.3em] text-[#D5A8A4] mb-3">
              {t("captureFirst.privacyEyebrow")}
            </span>
            <h2 className="font-serif italic text-4xl sm:text-5xl leading-tight mb-6">
              {t("captureFirst.privacyHeadline")}
            </h2>
            <p className="text-lg text-white/85 leading-relaxed mb-6">
              {t("captureFirst.privacyBody")}
            </p>
            <p className="text-sm text-white/65 leading-relaxed italic">
              {t("captureFirst.privacySupport")}
            </p>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {privacyPoints.map((p) => (
              <li
                key={p}
                className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-2xl p-4"
                data-testid={`landing-privacy-point-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <ShieldCheck className="h-4 w-4 text-[#D5A8A4] mt-1 shrink-0" />
                <span className="text-sm text-white/90 leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ───────────── PRICING ───────────── */}
      {!readerApp && (
        <section
          id="pricing"
          className="px-6 sm:px-10 py-20 sm:py-28"
          data-testid="landing-pricing"
        >
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block text-[11px] uppercase tracking-[0.3em] text-[#9B6A6C] mb-3">
                {t("captureFirst.pricingEyebrow")}
              </span>
              <h2 className="font-serif italic text-4xl sm:text-5xl leading-tight text-[#2C2123] mb-4">
                {t("captureFirst.pricingHeadline")}
              </h2>
              <p className="text-lg text-[#5C4B51] leading-relaxed max-w-xl mx-auto">
                {t("captureFirst.pricingBody")}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              {/* FREE — headline tier, gets the brighter card */}
              <div
                className="bg-white border-2 border-[#9B6A6C] rounded-3xl p-8 soft-shadow-lg relative"
                data-testid="landing-plan-free"
              >
                <span className="absolute -top-3 left-8 bg-[#9B6A6C] text-white text-[10px] uppercase tracking-[0.22em] px-3 py-1 rounded-full">
                  {t("captureFirst.pricingFreeTier")}
                </span>
                <div className="flex items-baseline gap-1 mb-2 mt-2">
                  <span className="text-5xl font-light text-[#2C2123]">
                    {t("captureFirst.pricingFreePrice")}
                  </span>
                  <span className="text-[#8A7D82] ml-1">
                    / {t("captureFirst.pricingFreeCadence")}
                  </span>
                </div>
                <ul className="space-y-3 mb-8 mt-6">
                  {[
                    "pricingFreeFeature1",
                    "pricingFreeFeature2",
                    "pricingFreeFeature3",
                    "pricingFreeFeature4",
                    "pricingFreeFeature5",
                  ].map((k) => (
                    <li
                      key={k}
                      className="flex items-start gap-2 text-[#2C2123]"
                    >
                      <Check className="h-4 w-4 text-[#9B6A6C] mt-1 shrink-0" />
                      <span>{t(`captureFirst.${k}`)}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={primaryCtaTo}
                  onClick={onPrimaryCta}
                  className="inline-flex items-center justify-center gap-2 w-full bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-6 py-3.5 text-base font-medium transition-all"
                  data-testid="landing-plan-free-cta"
                >
                  {t("captureFirst.pricingFreeCta")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              {/* PRO — quieter card so free stays the headline */}
              <div
                className="bg-[#FDFBF7] border border-[#EADDD7] rounded-3xl p-8 soft-shadow"
                data-testid="landing-plan-pro"
              >
                <span className="inline-block text-[#9B6A6C] text-[10px] uppercase tracking-[0.22em] px-1 py-1 mb-2">
                  {t("captureFirst.pricingProTier")}
                </span>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-5xl font-light text-[#2C2123]">
                    {t("captureFirst.pricingProPrice")}
                  </span>
                  <span className="text-[#8A7D82] ml-1">
                    / {t("captureFirst.pricingProCadence")}
                  </span>
                </div>
                <ul className="space-y-3 mb-8 mt-6">
                  {[
                    "pricingProFeature1",
                    "pricingProFeature2",
                    "pricingProFeature3",
                    "pricingProFeature4",
                  ].map((k) => (
                    <li
                      key={k}
                      className="flex items-start gap-2 text-[#5C4B51]"
                    >
                      <Check className="h-4 w-4 text-[#9B6A6C] mt-1 shrink-0" />
                      <span>{t(`captureFirst.${k}`)}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center gap-2 w-full border border-[#D5A8A4] text-[#5D4050] hover:bg-[#F8EEEE] rounded-full px-6 py-3.5 text-base transition-all"
                  data-testid="landing-plan-pro-cta"
                >
                  {t("captureFirst.pricingProCta")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ───────────── FOUNDER SHORT BIO (lower in funnel) ───────────── */}
      <section
        className="px-6 sm:px-10 py-16 sm:py-20 bg-[#FBF4EF] border-y border-[#EADDD7]"
        data-testid="landing-founder-short"
      >
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block text-[11px] uppercase tracking-[0.3em] text-[#9B6A6C] mb-3">
            {t("captureFirst.founderEyebrow")}
          </span>
          <h3 className="font-serif italic text-3xl sm:text-4xl leading-tight text-[#2C2123] mb-5">
            {t("captureFirst.founderHeadline")}
          </h3>
          <p className="text-base text-[#5C4B51] leading-relaxed mb-6 max-w-2xl mx-auto">
            {t("captureFirst.founderShortBio")}
          </p>
          <a
            href="#founder-deep"
            onClick={(e) => {
              e.preventDefault();
              // Founder deep-story lives on /about — this is the
              // overflow link, not a top-of-page surface.
              window.location.href = "/about";
            }}
            className="inline-flex items-center gap-1.5 text-sm text-[#9B6A6C] hover:text-[#5D4050] underline-offset-4 hover:underline transition-colors"
            data-testid="landing-founder-read-more"
          >
            {t("captureFirst.founderReadMore")}
            <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </section>

      {/* ───────────── FINAL REASSURANCE ───────────── */}
      <section className="px-6 sm:px-10 py-16 sm:py-24">
        <div className="max-w-2xl mx-auto text-center">
          <Link
            to={primaryCtaTo}
            onClick={onPrimaryCta}
            className="group inline-flex items-center justify-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-8 py-4 text-base font-medium transition-all shadow-[0_8px_24px_rgba(155,106,108,0.38)] hover:-translate-y-0.5"
            data-testid="welcome-launch-app-footer-btn"
          >
            {t("captureFirst.finalReassureCta")}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <p
            className="mt-4 text-xs text-[#8A7D82]"
            data-testid="landing-final-privacy-line"
          >
            {t("captureFirst.finalReassurePrivacy")}
          </p>
        </div>
      </section>

      <Footer />

      <PrivacyIntro forceOpen={showIntro} onClose={onCloseIntro} />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// CaptureFirstHeader — slim guest-only header
// ─────────────────────────────────────────────────────────────
// Replaces the standard <Header /> for the capture-first landing.
// Per the Feb 2026 redesign spec, only three things stay visible:
//   1. Logo
//   2. Primary CTA — "Start my free routine"
//   3. Overflow trigger (hamburger) that opens a side panel with
//      Sign in / Pricing / Resources / Press / Investors / etc.
// This intentionally removes Pricing + Resources from the visible
// nav so guest attention has exactly ONE call to action.
const CaptureFirstHeader = ({ readerApp }) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const panelRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [menuOpen]);

  const handleStartRoutine = (e) => {
    // The CaptureFirstHeader sits *inside* GuestCaptureFirstLanding so
    // the parent's `onPrimaryCta` already minted a guest session. From
    // here we just navigate to onboarding — the click telemetry is
    // fired by the hero buttons which carry the canonical event. This
    // header click is intentionally a quieter signal; keep it simple.
    e.preventDefault();
    setMenuOpen(false);
    trackFP("homepage_primary_cta_clicked", {
      auth: "guest",
      source: "header",
    });
    navigate("/onboarding");
  };

  // Overflow links — these are the things explicitly demoted per
  // the redesign spec. Sign-in was promoted out of the overflow to a
  // visible header pill (Feb 16, 2026) per founder feedback: returning
  // users need a one-tap, no-hamburger-required path back in.
  const overflowLinks = [
    !readerApp && {
      to: "/pricing",
      label: t("nav.pricing"),
      testid: "overflow-pricing",
    },
    { to: "/resources", label: t("nav.resources"), testid: "overflow-resources" },
    { to: "/about", label: t("captureFirst.navContactFounder"), testid: "overflow-contact" },
    { to: "/pitch", label: t("captureFirst.navInvestors"), testid: "overflow-investors" },
  ].filter(Boolean);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-[#EADDD7]/60"
      data-testid="capture-first-header"
    >
      <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 gap-3">
        <Link
          to="/"
          className="flex items-center gap-2 sm:gap-2.5 shrink-0 min-w-0"
          data-testid="logo-home-link"
        >
          <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center shadow-[0_4px_12px_rgba(155,106,108,0.28)] shrink-0">
            <SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
          </div>
          <span className="hidden min-[380px]:inline font-serif text-2xl sm:text-3xl tracking-tight text-[#2C2123] leading-none">
            TruBeauté
          </span>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Sign in — promoted out of the overflow drawer (Feb 16, 2026).
              Returning users need a one-tap path back in without
              hunting through the hamburger. Visible on every viewport
              (small text on mobile so it doesn't crowd the primary
              CTA, full pill style on desktop).
              Hidden in public demo mode (demo.trubeaute.com) — demos
              use ephemeral demo users; sign-in is intentionally
              suppressed so nothing attaches to a real account. */}
          {!(typeof window !== "undefined" && window.__trubeaute_demo_mode) && (
            <Link
              to="/login"
              className="text-xs sm:text-sm text-[#5C4B51] hover:text-[#9B6A6C] transition-colors px-1.5 sm:px-3 py-2 whitespace-nowrap"
              data-testid="header-signin-link"
              onClick={() =>
                trackFP("homepage_signin_clicked", { source: "header" })
              }
            >
              {t("nav.signIn")}
            </Link>
          )}
          <button
            type="button"
            onClick={handleStartRoutine}
            className="hidden sm:inline-flex items-center gap-2 bg-[#9B6A6C] text-white hover:bg-[#805557] rounded-full px-5 py-2.5 text-sm font-medium transition-all shadow-[0_4px_14px_rgba(155,106,108,0.32)] hover:-translate-y-0.5"
            data-testid="header-start-routine-btn"
          >
            <SparklesIcon className="h-3.5 w-3.5" />
            {t("captureFirst.navStartRoutine")}
          </button>
          {/* On mobile we shorten the CTA so the hamburger has room. */}
          <button
            type="button"
            onClick={handleStartRoutine}
            className="sm:hidden inline-flex items-center gap-1.5 bg-[#9B6A6C] text-white rounded-full px-3 py-2 text-xs font-medium shadow-[0_4px_14px_rgba(155,106,108,0.32)]"
            data-testid="header-start-routine-btn-mobile"
            aria-label={t("captureFirst.heroPrimaryCta")}
          >
            <SparklesIcon className="h-3 w-3" />
            <span>{t("captureFirst.navStartShort")}</span>
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="h-10 w-10 rounded-full border border-[#EADDD7] hover:border-[#D5A8A4] bg-white text-[#5C4B51] flex items-center justify-center transition-colors"
            aria-expanded={menuOpen}
            aria-label={t("captureFirst.navOverflowAria")}
            data-testid="header-overflow-trigger"
          >
            {menuOpen ? (
              <X className="h-4 w-4" />
            ) : (
              <Menu className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Overflow drawer — slides in from the right; covers ~85% on
          mobile, ~360px on desktop. Contains the demoted links. */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-[60] bg-[#2C2123]/40 backdrop-blur-sm"
          aria-hidden="true"
        >
          <motion.aside
            ref={panelRef}
            initial={{ x: 320, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="absolute top-0 right-0 h-full w-[85vw] sm:w-[360px] bg-white shadow-[0_-8px_30px_rgba(44,33,35,0.2)] overflow-y-auto"
            role="dialog"
            aria-modal="true"
            data-testid="header-overflow-panel"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#EADDD7]">
              <span className="text-[11px] uppercase tracking-[0.22em] text-[#9B6A6C]">
                {t("captureFirst.footerOverflowLabel")}
              </span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="h-9 w-9 rounded-full hover:bg-[#F5EFEB] flex items-center justify-center text-[#8A7D82] hover:text-[#2C2123] transition-colors"
                data-testid="header-overflow-close"
                aria-label={t("common.close")}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="py-3" aria-label={t("captureFirst.navOverflowAria")}>
              {overflowLinks.map((link) => (
                <Link
                  key={link.testid}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-between px-6 py-4 text-base text-[#2C2123] hover:bg-[#F5EFEB] transition-colors"
                  data-testid={link.testid}
                >
                  <span>{link.label}</span>
                  <ChevronRight className="h-4 w-4 text-[#8A7D82]" />
                </Link>
              ))}
            </nav>
          </motion.aside>
        </div>
      )}
    </header>
  );
};
